import React, { useEffect, useState } from "react";
import { Col, Table, Spinner, Badge, Row, Card, Container } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { authHeader } from "../../../services/auth-header";
import { BASE_URL } from "../../../services/Api";
import Swal from "sweetalert2";
import { FaArrowLeft } from "react-icons/fa";

const PPSIView = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const [isLoading, setIsLoading] = useState(true);
    const [purchaseData, setPurchaseData] = useState(null);
    const [exportchargescurrency, setExportchargescurrency] = useState(0);
    const [rows, setRows] = useState([]);

    const fetchPurchaseData = async () => {
        try {
            const response = await axios.get(`${BASE_URL}purchase/findById/${id}`, {
                headers: authHeader()
            });

            const data = response.data;
            setPurchaseData(data);
            setExportchargescurrency(data.exportchargescurrency || 0);

            if (data.purchaseItem && data.purchaseItem.length > 0) {
                setRows(data.purchaseItem.map(item => ({
                    description: item.product.description,
                    productremarks: item.productremarks || "",
                    quantity: item.quantity,
                    unitratecurrency: item.unitratecurrency,
                    totalcurrency: item.totalcurrency,
                    exchangerate: item.exchangerate,
                    unitRateInr: item.unitrateinr,
                    totalInr: item.totalinr,
                    hsnsacno: item.hsnsacno,
                    partno: item.partno,
                    serialnumber: item.serialnumber,
                    modelnumber: item.modelnumber,
                    tagnumber: item.tagnumber,
                    moc: item.moc || "",
                    itemcode: item.itemcode || "",
                    uom: item.uom,
                    currency: item.currency || "$ (USD)"
                })));
            }
        } catch (error) {
            console.error("Error fetching purchase data", error);
            Swal.fire({
                icon: "error",
                title: "Error",
                text: "Failed to fetch purchase data. Please try again.",
            });
            navigate("/pindex/1");
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchPurchaseData();
    }, [id]);

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-IN');
    };

    const getStatusBadge = (status) => {
        switch (status) {
            case 'Draft':
                return 'secondary';
            case 'Submitted':
                return 'primary';
            case 'Approved':
                return 'success';
            case 'Rejected':
                return 'danger';
            case 'Completed':
                return 'info';
            case 'Open':
                return 'warning';
            default:
                return 'light';
        }
    };

    const formatCurrency = (value) =>
        new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 2,
        }).format(value || 0);

    const formatForeignCurrency = (value, currency = '$ (USD)') => {
        if (!value) return '-';
        const currencySymbol = currency.includes('$') ? '$' :
            currency.includes('€') ? '€' :
                currency.includes('¥') ? '¥' :
                    currency.includes('£') ? '£' : '₹';
        return `${currencySymbol} ${parseFloat(value).toFixed(2)}`;
    };

    if (isLoading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ height: "100vh" }}>
                <Spinner animation="border" variant="primary" />
            </div>
        );
    }

    if (!purchaseData) {
        return <div className="alert alert-danger text-center mt-4">Purchase data not found</div>;
    }

    // Calculate totals
    const netTotalINR = rows.reduce((total, row) => total + (parseFloat(row.totalInr) || 0), 0);
    const netTotalCurrency = rows.reduce((total, row) => total + (parseFloat(row.totalcurrency) || 0), 0);
    const exportChargesINR = (exportchargescurrency * (purchaseData.purchaseexchangerate || 0));
    const grandTotalINR = netTotalINR + exportChargesINR;
    const grandTotalCurrency = netTotalCurrency + exportchargescurrency;

    return (
        <Container fluid className="p-3">
            {/* Header Section */}
            <Card className="shadow-sm border-0 rounded mb-3">
                <Card.Body className="p-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                        <h5 className="fw-bold text-primary mb-0">INTERNATIONAL PURCHASE - VIEW</h5>
                        <button className="btn btn-secondary btn-sm" onClick={() => navigate(-1)}>
                            <FaArrowLeft className="me-1" />
                            Back
                        </button>
                    </div>

                    <Row className="g-2">
                        <Col md={3}>
                            <div className="border-start border-3 border-primary ps-2">
                                <small className="text-muted">Purchase Invoice No</small>
                                <div className="fw-bold">{purchaseData.purchaseinvoiceno || '-'}</div>
                            </div>
                        </Col>
                        <Col md={3}>
                            <div className="border-start border-3 border-success ps-2">
                                <small className="text-muted">Purchase Date</small>
                                <div className="fw-bold">{formatDate(purchaseData.purchaseinvoicedate)}</div>
                            </div>
                        </Col>
                        <Col md={3}>
                            <div className="border-start border-3 border-warning ps-2">
                                <small className="text-muted">Currency</small>
                                <div className="fw-bold">{purchaseData.purchasecurrency || '$ (USD)'}</div>
                            </div>
                        </Col>
                        <Col md={3}>
                            <div className="border-start border-3 border-info ps-2">
                                <small className="text-muted">Status</small>
                                <div>
                                    <Badge
                                        bg={getStatusBadge(purchaseData.status)}
                                        className="px-2 py-1"
                                    >
                                        {purchaseData.status || '-'}
                                    </Badge>
                                </div>
                            </div>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {/* Supplier Information */}
            <Card className="shadow-sm border-0 rounded mb-3">
                <Card.Header className="bg-light py-2">
                    <h6 className="mb-0 fw-bold">Supplier Details</h6>
                </Card.Header>
                <Card.Body className="p-3">
                    <Row className="g-3">
                        <Col md={4}>
                            <div className="mb-2">
                                <small className="text-muted">Company Name</small>
                                <div className="fw-bold">{purchaseData.supplier?.companyname || '-'}</div>
                            </div>
                            <div>
                                <small className="text-muted">Contact Person</small>
                                <div className="fw-bold">{purchaseData.contactperson || '-'}</div>
                            </div>
                        </Col>
                        <Col md={4}>
                            <div className="mb-2">
                                <small className="text-muted">Phone</small>
                                <div className="fw-bold">{purchaseData.contactno || '-'}</div>
                            </div>
                            <div>
                                <small className="text-muted">Email</small>
                                <div className="fw-bold">{purchaseData.email || '-'}</div>
                            </div>
                        </Col>
                        <Col md={4}>
                            <div>
                                <small className="text-muted">Address</small>
                                <div className="fw-bold">
                                    {purchaseData.supplierMultipleAddress?.supplieraddress || '-'}
                                </div>
                            </div>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {/* Invoice & Delivery Information */}
            <Card className="shadow-sm border-0 rounded mb-3">
                <Card.Header className="bg-light py-2">
                    <h6 className="mb-0 fw-bold">Invoice & Delivery Information</h6>
                </Card.Header>
                <Card.Body className="p-3">
                    <Row className="g-3">
                        <Col md={4}>
                            <div className="mb-2">
                                <small className="text-muted">Supplier Invoice No</small>
                                <div className="fw-bold">{purchaseData.supplierinvoiceno || '-'}</div>
                            </div>
                            <div>
                                <small className="text-muted">Supplier Invoice Date</small>
                                <div className="fw-bold">{formatDate(purchaseData.supplierinvoicedate)}</div>
                            </div>
                        </Col>
                        <Col md={4}>
                            <div className="mb-2">
                                <small className="text-muted">Branch</small>
                                <div className="fw-bold">{purchaseData.branch?.branchname || '-'}</div>
                            </div>
                            <div>
                                <small className="text-muted">Warehouse</small>
                                <div className="fw-bold">{purchaseData.warehouse?.warehousename || '-'}</div>
                            </div>
                        </Col>
                        <Col md={4}>
                            <div className="mb-2">
                                <small className="text-muted">Other Reference</small>
                                <div className="fw-bold">{purchaseData.otherreference || '-'}</div>
                            </div>
                            <div>
                                <small className="text-muted">Exchange Rate</small>
                                <div className="fw-bold">{purchaseData.purchaseexchangerate || '0.00'}</div>
                            </div>
                        </Col>
                        <Col md={6}>
                            <div className="mb-2">
                                <small className="text-muted">Delivery Note No</small>
                                <div className="fw-bold">{purchaseData.deliverynoteno || '-'}</div>
                            </div>
                        </Col>
                        <Col md={6}>
                            <div className="mb-2">
                                <small className="text-muted">Delivery Note Date</small>
                                <div className="fw-bold">{formatDate(purchaseData.deliverynotedate)}</div>
                            </div>
                        </Col>
                        <Col md={12}>
                            <div className="mt-2">
                                <small className="text-muted">Delivery Address</small>
                                <div className="fw-bold">{purchaseData.shipto || '-'}</div>
                            </div>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {/* Purchase Items */}
            <Card className="shadow-sm border-0 rounded mb-3">
                <Card.Header className="bg-light py-2">
                    <h6 className="mb-0 fw-bold">Purchase Items</h6>
                </Card.Header>
                <Card.Body className="p-0">
                    <div className="table-responsive">
                        <Table bordered size="sm" className="mb-0">
                            <thead className="table-light">
                                <tr>
                                    <th className="text-center fw-bold py-2">#</th>
                                    <th className="fw-bold py-2">Product Description</th>
                                    <th className="fw-bold py-2">Serial No</th>
                                    <th className="fw-bold py-2">Model No</th>
                                    <th className="fw-bold py-2">Tag No</th>
                                    <th className="fw-bold py-2">Part No</th>
                                    <th className="fw-bold py-2">MOC</th>
                                    <th className="fw-bold py-2">Item Code</th>
                                    <th className="fw-bold py-2">HSN Code</th>
                                    <th className="fw-bold py-2 text-center">Qty</th>
                                    <th className="fw-bold py-2 text-center">UOM</th>
                                    <th className="fw-bold py-2 text-end">Unit Price (FC)</th>
                                    <th className="fw-bold py-2 text-end">Total (FC)</th>
                                    <th className="fw-bold py-2 text-end">Unit Price ₹</th>
                                    <th className="fw-bold py-2 text-end">Total ₹</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((row, index) => (
                                    <tr key={index}>
                                        <td className="text-center">{index + 1}</td>
                                        <td>
                                            <div className="fw-semibold">{row.description}</div>
                                            {row.productremarks && (
                                                <div className="text-muted small mt-1">
                                                    <small><strong>Remarks:</strong> {row.productremarks}</small>
                                                </div>
                                            )}
                                        </td>
                                        <td>{row.serialnumber || '-'}</td>
                                        <td>{row.modelnumber || '-'}</td>
                                        <td>{row.tagnumber || '-'}</td>
                                        <td>{row.partno || '-'}</td>
                                        <td>{row.moc || '-'}</td>
                                        <td>{row.itemcode || '-'}</td>
                                        <td>{row.hsnsacno || '-'}</td>
                                        <td className="text-center">{row.quantity || '0'}</td>
                                        <td className="text-center">{row.uom || '-'}</td>
                                        <td className="text-end">{row.unitratecurrency?.toFixed(2) || '0.00'}</td>
                                        <td className="text-end fw-bold">{row.totalcurrency?.toFixed(2) || '0.00'}</td>
                                        <td className="text-end">{formatCurrency(row.unitRateInr)}</td>
                                        <td className="text-end fw-bold">{formatCurrency(row.totalInr)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                </Card.Body>
            </Card>

            {/* Summary Section */}
            <Row>
                <Col lg={6} className="ms-auto">
                    <Card className="shadow-sm border-0 rounded">
                        <Card.Header className="bg-light py-2">
                            <h6 className="mb-0 fw-bold text-center">Purchase Summary</h6>
                        </Card.Header>
                        <Card.Body className="p-3">
                            <div className="space-y-2">
                                <div className="d-flex justify-content-between align-items-center py-1 border-bottom">
                                    <span className="fw-semibold">Net Total (INR):</span>
                                    <span className="fw-bold">{formatCurrency(netTotalINR)}</span>
                                </div>

                                <div className="d-flex justify-content-between align-items-center py-1 border-bottom">
                                    <span className="fw-semibold">Net Total (Foreign Currency):</span>
                                    <span className="fw-bold">{formatForeignCurrency(netTotalCurrency, purchaseData.purchasecurrency)}</span>
                                </div>

                                <div className="d-flex justify-content-between align-items-center py-1 border-bottom">
                                    <span className="fw-semibold">Export Charges (FC):</span>
                                    <span className="fw-bold">{formatForeignCurrency(exportchargescurrency, purchaseData.purchasecurrency)}</span>
                                </div>

                                <div className="d-flex justify-content-between align-items-center py-1 border-bottom">
                                    <span className="fw-semibold">Export Charges (INR):</span>
                                    <span className="fw-bold">{formatCurrency(exportChargesINR)}</span>
                                </div>

                                <div className="d-flex justify-content-between align-items-center py-2 border-top">
                                    <span className="fw-bold">Grand Total (INR)</span>
                                    <span className="fw-bold text-primary">{formatCurrency(grandTotalINR)}</span>
                                </div>

                                <div className="d-flex justify-content-between align-items-center py-1 bg-light rounded px-2">
                                    <span className="fw-bold">Grand Total (FC)</span>
                                    <span className="fw-bold text-success">{formatForeignCurrency(grandTotalCurrency, purchaseData.purchasecurrency)}</span>
                                </div>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Footer Actions */}
            <div className="text-center mt-3 pt-2 border-top">
                <button
                    className="btn btn-secondary btn-sm"
                    onClick={() => navigate(-1)}
                >
                    <FaArrowLeft className="me-1" />
                    Back to List
                </button>
            </div>
        </Container>
    );
};

export default PPSIView;